import type { VideoProvider } from "../../../hooks/useResources";
import styles from "./VideoEmbed.module.css";

type Props = {
  provider: VideoProvider;
  videoId: string;
  title?: string;
};

// Only these two origins are ever framed. The backend already restricts
// `videoUrl` to the same allowlist, so this switch is exhaustive by
// construction rather than a fallback for arbitrary input.
const embedSrc = (provider: VideoProvider, videoId: string): string => {
  const id = encodeURIComponent(videoId);
  switch (provider) {
    case "loom":
      return `https://www.loom.com/embed/${id}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`;
    case "youtube":
      // youtube-nocookie.com defers YouTube's tracking cookies until the
      // viewer actually presses play. rel=0 keeps the end-of-video grid to
      // the same channel instead of surfacing unrelated recommendations.
      return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`;
  }
};

// YouTube needs encrypted-media for DRM-backed playback and web-share for its
// share control; Loom's player wants neither.
const embedAllow = (provider: VideoProvider): string =>
  provider === "youtube"
    ? "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
    : "fullscreen; autoplay";

const providerTitle = (provider: VideoProvider): string =>
  provider === "youtube" ? "YouTube video player" : "Loom video player";

export const VideoEmbed = ({ provider, videoId, title }: Props): JSX.Element => (
  <div className={styles.frame}>
    <iframe
      src={embedSrc(provider, videoId)}
      title={title ?? providerTitle(provider)}
      allow={embedAllow(provider)}
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      className={styles.iframe}
      loading="lazy"
    />
  </div>
);

export const PROVIDER_LABEL: Readonly<Record<VideoProvider, string>> = {
  loom: "Loom",
  youtube: "YouTube",
};

const LOOM_THUMB_PREFIX = "https://cdn.loom.com/sessions/thumbnails/";

// Thumbnails the dashboard can derive from an id alone, used when no thumbnail
// was stored at create time.
//
// YouTube's maxresdefault is a clean 16:9 still but only exists for uploads
// that had a 720p source; hqdefault (always present, but a 16:9 frame
// letterboxed into 4:3) is the fallback. Callers that render these should
// chain through `videoThumbnailFallback` on an image error. YouTube resources
// never store a thumbnail unless an admin typed one, so this chain is what a
// YouTube card normally renders.
export const videoThumbnailUrl = (
  provider: VideoProvider,
  videoId: string,
): string => {
  const id = encodeURIComponent(videoId);
  return provider === "youtube"
    ? `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`
    : `${LOOM_THUMB_PREFIX}${id}-with-play.gif`;
};

export const videoThumbnailFallback = (
  provider: VideoProvider,
  videoId: string,
): string | null =>
  provider === "youtube"
    ? `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`
    : null;
